import { inject, Injectable } from '@angular/core';
import { ApiResponseModel, ApiService } from '@solcre-org/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment.local';
import { PortModel } from '../models/port.model';
import { PortCallModel } from '../models/port-call.model';
import { BookingItemModel } from '../models/booking-item.model';
import { VoyageSummaryModel } from '../models/voyage-summary.model';
import { VoyageSegmentModel } from '../models/voyage-segment.model';
import { PortCallDetails } from '../interfaces/portcall-details.interface';
import { VoyageExpensesSegmentModel } from '../models/voyage-expenses-segment.model';
import { VesselFuelModel } from '../models/vessel-fuel.model';

@Injectable({
  providedIn: 'root',
})
export class VoyageService {
  private apiService: ApiService = inject(ApiService);

  /**
   * Fetches all port calls for a given voyage.
   *
   * @param voyageId - The ID of the voyage whose port calls to retrieve.
   * @returns An observable emitting the list of `PortCallModel` instances.
   */
  public fetchPortCallsByVoyageId(voyageId: string): Observable<PortCallModel[]> {
    return this.apiService
      .fetchData(environment.API.VOYAGES_PORTCALL_URI.replace(':id', voyageId))
      .pipe(
        map((response: ApiResponseModel) => {
          return new PortCallModel().fromJSONList(response.data?.data ?? []);
        })
      );
  }

  public fetchVoyageSummary(voyageId: string): Observable<VoyageSummaryModel> {
    return this.apiService
      .fetchData(environment.API.VOYAGES_SUMMARY_URI.replace(':id', voyageId))
      .pipe(
        map((response: ApiResponseModel) => {
          const json: any = response.data;
          json.id = voyageId;
          const summary = new VoyageSummaryModel().fromJSON(json);
          return summary;
        })
      );
  }

  public fetchPorts(): Observable<PortModel[]> {
    return this.apiService.fetchData(environment.API.PORTS_URI + '?pagination=false&order[fullName]=asc').pipe(
      map((response: ApiResponseModel) => {
        return new PortModel().fromJSONList(response.data?.data ?? []);
      })
    );
  }

  public fetchPortCallDetails(portCallId: string): Observable<PortCallDetails> {
    return this.apiService
      .fetchData(
        environment.API.VOYAGES_PORTCALL_DETAILS_URI.replace(':id', portCallId)
      )
      .pipe(
        map((response: ApiResponseModel) => {
          //Create model response
          const segments: any = response.data?.segments;
          const resp: PortCallDetails = {
            arrival: null,
            departure: null,
            expenses: null,
          };

          //Check each key
          if (segments) {
            if (segments?.arrival) {
              resp.arrival = new VoyageSegmentModel();
              resp.arrival.fromJSON(segments?.arrival);
            }
            if (segments?.departure) {
              resp.departure = new VoyageSegmentModel();
              resp.departure.fromJSON(segments?.departure);
            }
            if (segments?.expenses) {
              resp.expenses = new VoyageExpensesSegmentModel();
              resp.expenses.fromJSON(segments?.expenses);
            }
          }
          return resp;
        })
      );
  }

  public createPortCall(
    voyageId: string,
    portCall: any
  ): Observable<PortCallModel> {
    return this.apiService
      .createObj(
        environment.API.VOYAGES_PORTCALL_URI.replace(':id', voyageId),
        portCall
      )
      .pipe(
        map((response: ApiResponseModel) => {
          return new PortCallModel().fromJSON(response.data);
        })
      );
  }

  public updatePortCall(
    voyageId: string,
    portCallId: string,
    portCall: any
  ): Observable<PortCallModel> {
    return this.apiService
      .partialUpdateObj(
        environment.API.VOYAGES_PORTCALL_URI.replace(':id', voyageId),
        portCallId,
        portCall,
        false,
        { 'Content-Type': 'application/merge-patch+json' }
      )
      .pipe(
        map((response: ApiResponseModel) => {
          return new PortCallModel().fromJSON(response.data);
        })
      );
  }

  public deletePortCall(
    voyageId: string,
    portCallId: string
  ): Observable<boolean> {
    return this.apiService.deleteObj(
      environment.API.VOYAGES_PORTCALL_URI.replace(':id', voyageId),
      portCallId
    );
  }

  public updatePortCallNauticalMileDistance(
    voyageId: string,
    portCallId: string,
    nauticalMileDistance: string | number
  ): Observable<PortCallModel> {
    // Reuse ApiService so the request keeps the same auth/header behavior as the rest of the app.
    // The backend expects the action as a sub-resource of the port-call URI.
    return this.apiService
      .partialUpdateObj(
        environment.API.VOYAGES_PORTCALL_URI.replace(':id', voyageId),
        // Compose the nested resource path: /{portCallId}/nautical-mile-distance
        `${portCallId}/nautical-mile-distance`,
        {
          // Always send a normalized 2-decimal payload to match the API contract.
          nauticalMileDistance: Number(nauticalMileDistance).toFixed(2),
        },
        false,
        { 'Content-Type': 'application/merge-patch+json' }
      )
      .pipe(
        map((response: ApiResponseModel) => {
          // Keep the service return type aligned with the rest of the voyage updates.
          return new PortCallModel().fromJSON(response.data);
        })
      );
  }

  public bookingItemRecalculateTotalCubicMeters(
    item: BookingItemModel
  ): number {
    return (item.length ?? 0) * (item.width ?? 0) * (item.height ?? 0);
  }

  public fetchVesselFuels(vesselId: string): Observable<VesselFuelModel[]> {
    return this.apiService.fetchData(environment.API.VESSELS_FUEL_URI.replace(':id', vesselId)).pipe(
      map((response: ApiResponseModel) => {
        return new VesselFuelModel().fromJSONList(response.data?.data ?? []);
      })
    );
  }
}
